/**
 * Optimized Image Component with WebP support and fallback
 * Automatically uses WebP when available, falls back to original format
 */
import { useState } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  onError?: () => void;
}

export function OptimizedImage({
  src,
  alt,
  className = '',
  width,
  height,
  loading = 'lazy',
  onError
}: OptimizedImageProps) {
  const [imageError, setImageError] = useState(false);

  // Check if the image is SVG (no need for WebP conversion)
  const isSvg = src.endsWith('.svg');

  // Generate WebP version path by replacing extension
  const getWebPPath = (imagePath: string): string => {
    if (isSvg) return imagePath;

    // Handle Firebase Storage URLs with query parameters
    const urlParts = imagePath.split('?');
    const pathPart = urlParts[0];
    const queryPart = urlParts[1] ? `?${urlParts[1]}` : '';

    // Replace extension in the path part
    const lastDot = pathPart.lastIndexOf('.');
    if (lastDot === -1) return imagePath;

    const webpPath = pathPart.substring(0, lastDot) + '.webp';
    return webpPath + queryPart;
  };

  const webpSrc = getWebPPath(src);

  const handleError = () => {
    setImageError(true);
    if (onError) onError();
  };

  // If it's SVG or image error occurred, use original src only
  if (isSvg || imageError) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        onError={handleError}
      />
    );
  }

  // Use picture element with WebP and fallback
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        onError={handleError}
      />
    </picture>
  );
}
